import React from 'react';
import { ArrowLeft, Star, Check, Edit3, Trash2, RotateCcw, ExternalLink } from 'lucide-react';
import { PLATFORM_COLORS, PLATFORM_TEXT, parseGenres, parseRatings } from '../constants';

export default function DetailView({ item, onBack, onEdit, onDelete, onMarkWatched, onMarkPending }) {
  const genres = parseGenres(item.genre);
  const rt = parseRatings(item.ratings_json);
  const isWatched = item.status === 'watched';

  return (
    <div className="min-h-screen bg-cinema-bg">
      {/* Header with back button */}
      <header className="bg-cinema-header/80 backdrop-blur-sm border-b border-cinema-border sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="flex items-center gap-2 text-steel-300 hover:text-white transition-colors h-9 sm:h-auto">
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Volver</span>
            </button>
            {item.imdb_rating && (
              <span className="sm:hidden w-9 h-9 rounded-lg bg-amber-400 text-amber-950 text-xs font-bold flex items-center justify-center shadow">
                {item.imdb_rating}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isWatched ? (
              <button onClick={() => onMarkWatched(item)}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-3 py-2 rounded-xl transition-all">
                <Check size={16} strokeWidth={3} /> Visto
              </button>
            ) : (
              <button onClick={() => onMarkPending(item)}
                className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium px-3 py-2 rounded-xl transition-all">
                <RotateCcw size={14} /> Pendiente
              </button>
            )}
            <button onClick={() => onEdit(item)}
              className="p-2 bg-cinema-accent/20 hover:bg-cinema-accent/30 text-cinema-accent rounded-xl transition-all">
              <Edit3 size={16} />
            </button>
            <button onClick={() => onDelete(item)}
              className="p-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-xl transition-all">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Poster */}
          <div className="w-full md:w-72 flex-shrink-0">
            {item.poster ? (
              <img src={item.poster} alt={item.title} className="w-full rounded-2xl shadow-2xl" />
            ) : (
              <div className="w-full aspect-[2/3] bg-steel-800 rounded-2xl flex items-center justify-center text-7xl">
                {item.type === 'series' ? '📺' : '🎬'}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{item.title}</h1>

            {/* Meta row */}
            <div className="flex items-center gap-3 text-steel-300 text-sm mb-4 flex-wrap">
              {item.year && <span>{item.year}</span>}
              {item.rated && <span className="px-2 py-0.5 bg-steel-700 rounded text-xs">{item.rated}</span>}
              {item.runtime && <span>{item.runtime} min</span>}
              {item.type === 'series' && item.total_seasons && <span>{item.total_seasons} temporadas</span>}
              <span>{item.type === 'series' ? '📺 Serie' : '🎬 Película'}</span>
            </div>

            {/* Ratings */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {item.imdb_rating && (
                <div className="flex items-center gap-1.5 bg-yellow-900/30 border border-yellow-700/30 px-3 py-1.5 rounded-xl">
                  <Star size={14} fill="currentColor" className="text-yellow-400" />
                  <span className="text-yellow-300 font-bold text-sm">{item.imdb_rating}</span>
                  <span className="text-yellow-600 text-xs">/10</span>
                </div>
              )}
              {rt.rt && (
                <div className="flex items-center gap-1 bg-red-900/20 border border-red-700/20 px-3 py-1.5 rounded-xl">
                  <span className="text-sm">🍅</span>
                  <span className="text-red-300 font-bold text-sm">{rt.rt}</span>
                </div>
              )}
              {item.metascore && item.metascore !== 'N/A' && (
                <div className="flex items-center gap-1 bg-green-900/20 border border-green-700/20 px-3 py-1.5 rounded-xl">
                  <span className="text-green-300 font-bold text-sm">MC</span>
                  <span className="text-green-300 text-sm">{item.metascore}</span>
                </div>
              )}
            </div>

            {/* Platform */}
            {item.platform && (
              <span className="inline-block px-3 py-1 rounded-lg text-sm font-medium platform-badge mb-4"
                style={{ background: PLATFORM_COLORS[item.platform] || '#333', color: PLATFORM_TEXT[item.platform] || '#fff' }}>
                {item.platform}
              </span>
            )}

            {/* Genres */}
            {genres.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-5">
                {genres.map(g => (
                  <span key={g} className="text-xs text-steel-300 bg-steel-700/50 px-2.5 py-1 rounded-lg">{g}</span>
                ))}
              </div>
            )}

            {/* Plot */}
            {item.plot && item.plot !== 'N/A' && (
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-steel-300 mb-1.5">Sinopsis</h3>
                <p className="text-steel-200 text-sm leading-relaxed">{item.plot}</p>
              </div>
            )}

            {/* Details grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {item.director && item.director !== 'N/A' && (
                <Detail label="Director" value={item.director} />
              )}
              {item.writer && item.writer !== 'N/A' && !item.director && (
                <Detail label="Guionista" value={item.writer} />
              )}
              {item.actors && item.actors !== 'N/A' && (
                <Detail label="Reparto" value={item.actors} />
              )}
              {item.country && item.country !== 'N/A' && (
                <Detail label="País" value={item.country} />
              )}
              {item.released && item.released !== 'N/A' && (
                <Detail label="Estreno" value={item.released} />
              )}
              {item.awards && item.awards !== 'N/A' && (
                <Detail label="🏆 Premios" value={item.awards} />
              )}
            </div>

            {/* IMDb link */}
            {item.imdb_id && (
              <a href={`https://www.imdb.com/title/${item.imdb_id}/`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-5 text-cinema-accent hover:text-cinema-accent-hover text-sm transition-colors">
                Ver en IMDb <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-cinema-card border border-cinema-border rounded-xl p-3">
      <p className="text-xs text-steel-400 mb-0.5">{label}</p>
      <p className="text-sm text-steel-100">{value}</p>
    </div>
  );
}
